import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Alert, Box, Button, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Select, Slider, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import HistoryIcon from '@mui/icons-material/History';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  getAlarmConfig, updateAlarmConfig, restoreDefaultAlarmConfig,
  uploadAlarmAudio, deleteAlarmAudio, getAlarmAudioHistory, restoreAlarmAudioVersion,
  getAlarmAudioUrl, testAlarm, getDevices, isSupervisor,
} from '../api/client';
import AlarmAssignmentPage from './AlarmAssignmentPage';
import InfoTooltip from '../components/InfoTooltip';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChanges';

const FEATURES = [
  { key: 'drowsiness', label: 'Drowsiness',  emoji: '😴', color: '#f44336', desc: 'Eyes closing / head drooping' },
  { key: 'yawning',    label: 'Yawning',     emoji: '🥱', color: '#ff9800', desc: 'Mouth opening wide' },
  { key: 'distracted', label: 'Distracted',  emoji: '👀', color: '#9c27b0', desc: 'Head turned away from camera' },
  { key: 'presence',   label: 'Presence',    emoji: '📍', color: '#00897b', desc: 'No face detected (never triggers on logout)' },
];

function FeatureCard({ feature, config, readOnly, devices, onSave, onRestoreDefault, onUpload,
                       onDeleteAudio, onOpenHistory, onTest }) {
  const [local, setLocal] = useState(config);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testDevice, setTestDevice] = useState('');
  const deviceNameById = Object.fromEntries((devices || []).map(d => [d.id, d.hostname || d.id]));

  useEffect(() => { setLocal(config); setDirty(false); }, [config]);

  const set = (field) => (value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(feature.key, {
        is_enabled: local.is_enabled,
        alarm_enabled: local.alarm_enabled,
        trigger_delay_seconds: local.trigger_delay_seconds,
        repeat_enabled: local.repeat_enabled,
        cooldown_seconds: local.cooldown_seconds,
        volume_percent: local.volume_percent,
      });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setLocal(config); setDirty(false); };
  const { confirmNavigation } = useUnsavedChangesGuard(dirty, { onSave: save, onDiscard: discard });
  const handleCancel = () => confirmNavigation(discard);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(feature.key, file);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTest(feature.key, testDevice || null);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, reason: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Accordion defaultExpanded={feature.key === 'drowsiness'}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 2 }}>
          <Typography fontSize="1.4rem">{feature.emoji}</Typography>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={700}>{feature.label}</Typography>
            <Typography variant="caption" color="text.secondary">{feature.desc}</Typography>
          </Box>
          <Chip size="small" label={local.is_enabled ? 'Detecting' : 'Off'}
                color={local.is_enabled ? 'success' : 'default'} />
          {local.is_enabled && (
            <Chip size="small" label={local.alarm_enabled ? 'Alarm on' : 'Silent'}
                  color={local.alarm_enabled ? 'success' : 'warning'} variant="outlined" />
          )}
          <Chip size="small" variant="outlined" label={`v${local.config_version ?? 1}`} />
          <Chip size="small" variant="outlined"
                label={`Assigned ${local.assigned_device_count ?? 0} / ${local.total_device_count ?? 0}`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Assigned Devices ({local.assigned_device_count ?? 0} / {local.total_device_count ?? 0})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(local.assigned_device_ids || []).slice(0, 5).map(id => (
                <Chip key={id} size="small" label={deviceNameById[id] || id} />
              ))}
              {(local.assigned_device_ids || []).length > 5 && (
                <Chip size="small" variant="outlined" label={`+${local.assigned_device_ids.length - 5} More`} />
              )}
              {(local.assigned_device_ids || []).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No devices assigned yet — supervisors assign devices in Alarm Assignment.
                </Typography>
              )}
            </Stack>
          </Box>

          <FormControlLabel
            control={<Switch checked={!!local.is_enabled} disabled={readOnly}
                            onChange={(e) => set('is_enabled')(e.target.checked)} />}
            label={<>Feature Enabled
              <InfoTooltip
                description="Enables detection for this feature."
                impact={['Detection stops', 'No warnings', 'No events', 'No alarms']}
                defaultValue="Off"
                saveRequired scope="future"
              /></>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Off = no detection, no timers, no events, no alarm for this feature.
          </Typography>

          <FormControlLabel
            control={<Switch checked={!!local.alarm_enabled} disabled={readOnly || !local.is_enabled}
                            onChange={(e) => set('alarm_enabled')(e.target.checked)} />}
            label={<>Alarm Enabled
              <InfoTooltip
                description="Controls alarm playback only. Detection continues even when alarms are disabled."
                defaultValue="On"
                saveRequired scope="future"
              /></>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Off = detection keeps running and events still reach the dashboard, but no sound plays.
          </Typography>

          <Box>
            <Typography variant="body2" gutterBottom>
              Trigger Delay: {local.trigger_delay_seconds}s
              <InfoTooltip
                description="Continuous detection required before an alarm is triggered."
                defaultValue="3s" saveRequired scope="future"
              />
            </Typography>
            <Slider value={local.trigger_delay_seconds} min={0} max={60} step={1}
                    disabled={readOnly} onChange={(_, v) => set('trigger_delay_seconds')(v)} />
          </Box>

          <FormControlLabel
            control={<Switch checked={!!local.repeat_enabled} disabled={readOnly}
                            onChange={(e) => set('repeat_enabled')(e.target.checked)} />}
            label={<>Repeat Alarm
              <InfoTooltip
                description="Repeats alarm after cooldown while detection continues."
                saveRequired scope="future"
              /></>}
          />

          <Box>
            <Typography variant="body2" gutterBottom>
              Cooldown: {local.cooldown_seconds}s
              <InfoTooltip
                description="Minimum delay between alarms."
                saveRequired scope="future"
              />
            </Typography>
            <Slider value={local.cooldown_seconds} min={0} max={300} step={5}
                    disabled={readOnly} onChange={(_, v) => set('cooldown_seconds')(v)} />
          </Box>

          <Box>
            <Typography variant="body2" gutterBottom>
              <VolumeUpIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Volume: {local.volume_percent}%
              <InfoTooltip
                description="Playback volume on assigned edge devices."
                saveRequired scope="future"
              />
            </Typography>
            <Slider value={local.volume_percent} min={0} max={100} step={5}
                    disabled={readOnly} onChange={(_, v) => set('volume_percent')(v)} />
          </Box>

          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Audio File
              <InfoTooltip
                description="Custom alarm audio synchronized to assigned devices."
                saveRequired={false} scope="future"
              />
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {local.audio ? (
                <>
                  <Typography variant="body2">{local.audio.original_filename}</Typography>
                  <audio controls preload="none" style={{ height: 32 }} src={getAlarmAudioUrl(feature.key)} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Using default sound</Typography>
              )}
            </Stack>
            {!readOnly && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Button size="small" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
                  {uploading ? <CircularProgress size={16} /> : (local.audio ? 'Replace' : 'Upload')}
                  <input type="file" hidden accept="audio/wav,audio/mpeg,audio/ogg,.wav,.mp3,.ogg"
                         onChange={handleFileChange} />
                </Button>
                {local.audio && (
                  <Button size="small" color="error" startIcon={<DeleteIcon />}
                          onClick={() => onDeleteAudio(feature.key)}>
                    Delete
                  </Button>
                )}
                <Button size="small" startIcon={<HistoryIcon />} onClick={() => onOpenHistory(feature.key)}>
                  Version History
                </Button>
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {!readOnly && (
              <>
                <Select size="small" value={testDevice} displayEmpty sx={{ minWidth: 180 }}
                        onChange={(e) => setTestDevice(e.target.value)}>
                  <MenuItem value="">All vendor devices</MenuItem>
                  {(devices || []).map(d => (
                    <MenuItem key={d.id} value={d.id}>{d.hostname || d.id}</MenuItem>
                  ))}
                </Select>
                <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />}
                        onClick={handleTest} disabled={testing}>
                  {testing ? <CircularProgress size={16} /> : 'Test Alarm'}
                </Button>
                <Button size="small" onClick={() => onRestoreDefault(feature.key)}>
                  Restore Default
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {dirty && (
                  <Chip size="small" color="warning" variant="outlined" label="Unsaved Changes" />
                )}
                <Button size="small" onClick={handleCancel} disabled={!dirty || saving}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={save} disabled={!dirty || saving}>
                  {saving ? <CircularProgress size={16} /> : 'Save'}
                </Button>
              </>
            )}
          </Stack>

          {testResult && (
            <Alert severity={testResult.success ? 'success' : 'error'} onClose={() => setTestResult(null)}>
              {testResult.success
                ? `Test alarm sent to ${testResult.notified} device(s).`
                : (testResult.reason || 'Test failed')}
            </Alert>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function VersionHistoryDialog({ feature, open, onClose, history, onRestore }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Audio Version History — {feature}</DialogTitle>
      <DialogContent>
        {!history ? <CircularProgress /> : history.length === 0 ? (
          <Alert severity="info">No audio has been uploaded for this feature yet.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell><TableCell>Filename</TableCell>
                <TableCell>Uploaded</TableCell><TableCell>Status</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell>v{row.version}</TableCell>
                  <TableCell>{row.original_filename}</TableCell>
                  <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{row.is_active ? <Chip size="small" color="success" label="Active" /> : 'Inactive'}</TableCell>
                  <TableCell>
                    {!row.is_active && (
                      <Tooltip title="Restore this version">
                        <IconButton size="small" onClick={() => onRestore(row.id)}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AlarmConfigPage() {
  // Configuration (this page) is vendor-admin-owned; Assignment (which
  // devices actually sound each alarm) is supervisor-owned — two separate
  // responsibilities, so a supervisor never sees this editable page at
  // all, only the read-only-configuration + editable-assignment one.
  // (A component choosing which child to mount is plain conditional
  // rendering, not a conditional hook call, so this stays outside — and
  // before — the hook-bearing implementation below.)
  if (isSupervisor()) return <AlarmAssignmentPage />;
  return <VendorAlarmConfigPage />;
}

function VendorAlarmConfigPage() {
  const readOnly = false;
  const [configs, setConfigs] = useState(null);
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState('');
  const [historyFeature, setHistoryFeature] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    getAlarmConfig()
      .then(setConfigs)
      .catch(e => setError(e.response?.data?.detail || 'Unable to load alarm configuration'));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getDevices().then(setDevices).catch(() => setDevices([])); }, []);

  const handleSave = async (feature, patch) => {
    await updateAlarmConfig(feature, patch);
    load();
  };
  const handleRestoreDefault = async (feature) => {
    await restoreDefaultAlarmConfig(feature);
    load();
  };
  const handleUpload = async (feature, file) => {
    await uploadAlarmAudio(feature, file);
    load();
  };
  const handleDeleteAudio = async (feature) => {
    await deleteAlarmAudio(feature);
    load();
  };
  const handleTest = async (feature, deviceId) => testAlarm(feature, deviceId);

  const openHistory = async (feature) => {
    setHistoryFeature(feature);
    setHistory(null);
    const rows = await getAlarmAudioHistory(feature);
    setHistory(rows);
  };
  const restoreVersion = async (audioFileId) => {
    await restoreAlarmAudioVersion(historyFeature, audioFileId);
    const rows = await getAlarmAudioHistory(historyFeature);
    setHistory(rows);
    load();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <NotificationsActiveIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Alarm Configuration</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per-feature alarm settings for every edge device on your vendor account.
        Changes apply automatically — no device restart required.
        {readOnly ? ' · read-only' : ''}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!configs ? <CircularProgress /> : (
        <Stack spacing={1}>
          {Object.keys(configs).length === 0 && (
            <Alert severity="info">
              No features are licensed for your vendor account yet — contact your platform administrator.
            </Alert>
          )}
          {/* Unlicensed features are absent from `configs` entirely (server-side
              filter) — a feature's card never renders unless it's licensed. */}
          {FEATURES.filter(feature => configs[feature.key]).map(feature => (
            <FeatureCard
              key={feature.key}
              feature={feature}
              config={configs[feature.key]}
              readOnly={readOnly}
              devices={devices}
              onSave={handleSave}
              onRestoreDefault={handleRestoreDefault}
              onUpload={handleUpload}
              onDeleteAudio={handleDeleteAudio}
              onOpenHistory={openHistory}
              onTest={handleTest}
            />
          ))}
        </Stack>
      )}

      <VersionHistoryDialog
        feature={historyFeature}
        open={!!historyFeature}
        onClose={() => setHistoryFeature(null)}
        history={history}
        onRestore={restoreVersion}
      />
    </Box>
  );
}
